import { Event, EventStatus } from '../types/types';
import { format, parseISO, startOfDay, subHours } from 'date-fns';
import { dateKeyInTz } from './timezone';

export type SportKind = 'CRICKET' | 'TABLE_TENNIS' | 'TENNIS' | 'OTHER';

/**
 * Resolves an event's sport to the kind the UI dispatches on. Matches on sport name
 * ('Cricket' / 'Table Tennis' / 'Tennis'), case-insensitively.
 */
export function sportOf(event?: Pick<Event, 'sport'> | null): SportKind {
  const name = event?.sport?.name?.toUpperCase();
  if (name === 'CRICKET') return 'CRICKET';
  if (name === 'TABLE TENNIS') return 'TABLE_TENNIS';
  if (name === 'TENNIS') return 'TENNIS';
  return 'OTHER';
}

/**
 * Groups events by date
 * @param events List of events to group
 * @param ruleSet Optional filter for specific rule set
 * @param timeZone IANA zone for day-bucketing ('' = browser local)
 * @returns Record with dates as keys and arrays of events as values
 */
export const groupEventsByDate = (events: Event[], ruleSet?: string, timeZone: string = ''): Record<string, Event[]> => {
  // Apply the rule set filter if needed
  const filteredEvents = ruleSet 
    ? events.filter(event => event.competition?.ruleSet === ruleSet)
    : events;

  // Group events by date
  const grouped = filteredEvents.reduce((acc, event) => {
    if (!event.startTime) return acc;
    
    const date = dateKeyInTz(event.startTime, timeZone); // Calendar date in the selected zone
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {} as Record<string, Event[]>);
  
  // Sort events within each date group by start time in descending order
  Object.keys(grouped).forEach(date => {
    grouped[date].sort((a, b) => {
      if (!a.startTime || !b.startTime) return 0;
      return new Date(b.startTime).getTime() - new Date(a.startTime).getTime(); // Descending order
    });
  });
  
  return grouped;
};

/**
 * Formats a date for display
 * @param dateString ISO date string
 * @param formatStr Format string for date-fns
 * @returns Formatted date string
 */
export const formatDate = (dateString: string, formatStr: string = 'EEEE, MMMM d, yyyy'): string => {
  return format(parseISO(dateString), formatStr);
};

/**
 * Formats a date string for display in the events list
 */
export function formatListDate(dateStr: string): string {
  return format(new Date(dateStr), 'MMM d, yyyy');
}

/**
 * Formats a date string for display with time
 */
export function formatDateTime(dateStr: string): string {
  return dateStr ? format(new Date(dateStr), 'yyyy-MM-dd HH:mm') : '';
}

/**
 * Gets the color associated with an event status
 */
export function getStatusColor(status: EventStatus): string {
  switch (status) {
    case EventStatus.ACTIVE: return 'green';
    case EventStatus.DISABLED_BY_PROVIDER: return 'orange';
    case EventStatus.DISABLED_BY_SYSTEM: return 'red';
    default: return 'gray';
  }
}

/**
 * Gets the background color for a stream ID
 * @param streamId Stream ID value between 1 and 16
 * @returns Background color for the stream ID
 */
export function getStreamIdBackgroundColor(streamId?: string): string {
  if (!streamId) return 'gray.100';
  
  const streamIdNum = parseInt(streamId, 10);
  if (isNaN(streamIdNum) || streamIdNum < 1 || streamIdNum > 16) return 'gray.100';
  
  // Define color mapping for stream IDs
  const colorMap: Record<number, string> = {
    1: 'red.100',
    2: 'orange.100',
    3: 'yellow.100',
    4: 'green.100',
    5: 'teal.100',
    6: 'blue.100',
    7: 'cyan.100',
    8: 'purple.100',
    9: 'pink.100',
    10: 'red.200',
    11: 'orange.200',
    12: 'yellow.200',
    13: 'green.200',
    14: 'teal.200',
    15: 'blue.200',
    16: 'purple.200'
  };
  
  return colorMap[streamIdNum] || 'gray.100';
}

/**
 * Gets the start time for event fetching based on selected date
 * @param selectedDate Selected date string in yyyy-MM-dd format
 * @returns Date object representing the start time
 */
export const getStartTimeForEvents = (selectedDate?: string): Date => {
  return selectedDate 
    ? startOfDay(parseISO(selectedDate))
    : subHours(new Date(), 6);
};
